import React, { FC } from 'react';

import { Collapse, Divider, Typography } from '@mui/material';
import Payment from '@solidgate/react-sdk';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import Loader from '~/components/atoms/Loader';
import PaymentError from '~/components/molecules/PaymentError';
import { usePaymentFormLogic } from '~/components/organisms/SolidCheckout/hooks/usePaymentFormLogic';
import { colors } from '~/theme/colors';
import { Typographies } from '~/theme/typography';
import { SOLID_PAYMENT_METHOD_MAPPER } from '~/types/payment';

import { sliderFormParams, sliderFormStyles } from './formConfig';
import styles from './styles.module.scss';
import { PaymentFormProps } from '../../../types';

const PaymentFormV1Sidekicker: FC<PaymentFormProps> = ({
  onPaymentSuccess,
  onPaymentFail,
  onPaymentSubmit,
  onCreateCardIntentError,
  onFormReady,
  paymentApi,
}) => {
  const { t } = useTranslation('general');
  const {
    isLoading,
    paymentError,
    merchantData,
    paypalBtnRef,
    appleContainerRef,
    googleContainerRef,
    mountedMethods,
    handleCloseError,
    handlePaymentSuccess,
    handlePaymentFail,
    handlePaymentSubmit,
    handleOnFormMounted,
  } = usePaymentFormLogic({
    paymentApi,
    onPaymentSuccess,
    onPaymentFail,
    onPaymentSubmit,
    onCreateCardIntentError,
    onFormReady,
  });

  const isOneClickShown =
    mountedMethods.applebtn ||
    mountedMethods.googlebtn ||
    mountedMethods.paypal;

  return (
    <div className={clsx(styles.container, styles.sidekicker)}>
      {isLoading && <Loader />}

      {paymentError && (
        <PaymentError
          paymentError={paymentError}
          handleCloseError={handleCloseError}
        />
      )}

      {merchantData && !paymentError && (
        <div>
          <Collapse in={isOneClickShown}>
            <div>
              <div
                className={clsx(styles['slide__pay_button'], styles.sidekicker)}
                ref={appleContainerRef}
              />
              <div
                className={clsx(styles['slide__pay_button'], styles.sidekicker)}
                ref={googleContainerRef}
              />
              <div
                className={clsx(styles['slide__pay_button'], styles.sidekicker)}
                id="paypal-button"
                ref={paypalBtnRef}
              />
            </div>

            <Divider
              sx={{
                margin: '16px 0',
                '& .MuiDivider-wrapper': {
                  padding: '0 10px',
                },
              }}
            >
              <Typography
                variant={Typographies.BODY_MEDIUM}
                color={colors.neutral_30}
              >
                {t('general:or_pay_with_card', 'Or pay with card')}
              </Typography>
            </Divider>
          </Collapse>

          <Payment
            googlePayContainerRef={googleContainerRef}
            applePayContainerRef={appleContainerRef}
            merchantData={merchantData}
            formParams={sliderFormParams(t)}
            styles={sliderFormStyles}
            onFail={(e): void =>
              handlePaymentFail(
                e.message || '',
                SOLID_PAYMENT_METHOD_MAPPER[e.entity],
              )
            }
            onSuccess={async (e): Promise<void> =>
              await handlePaymentSuccess(e.order)
            }
            onMounted={handleOnFormMounted}
            onSubmit={(e): void =>
              handlePaymentSubmit(SOLID_PAYMENT_METHOD_MAPPER[e.entity])
            }
          />
        </div>
      )}
    </div>
  );
};

export default PaymentFormV1Sidekicker;
